import { watch } from "vue";

import gaiupApolloClient from "@/graphql/services/ApolloClient";
import { useFlowGuideCoordinationStore } from "@/stores/flowGuide/flowGuideCoordination.store";
import { waitForHasuraAuthenticated } from "@/router/router";
import { HasuraNotAuthenticatedError } from "@/stores/hasura/hasuraErrors";
import { useIsDesktop } from "@/composables/useIsDesktop";
import { featureFlags } from "@/services/featureFlags";
import { WELCOME_FLOW_KEY } from "@/features/WelcomeFlow/welcomeFlow.config";
import {
  getWelcomeFlowRow,
  getWelcomeFlowRows,
  insertWelcomeFlowRow,
  markWelcomeFlowCompleted,
  recordWelcomeFlowEvent,
  reportSwallowedFailure,
  waitForModalStackToEmpty,
} from "@/features/WelcomeFlow/welcomeFlowShared";

import InsertUserPlatformReviewMutation from "@/graphql/mutations/InsertUserPlatformReviewMutation";
import { usePlatformReviewStore } from "./platformReview.store";
import {
  PLATFORM_REVIEW_FLOW_KEY,
  PLATFORM_REVIEW_MIN_GAP_MS,
  PLATFORM_REVIEW_NOT_NOW_KEY,
  PLATFORM_REVIEW_SHOW_FROM,
  PLATFORM_REVIEW_SHOW_KEYS,
} from "./platformReview.config";
import type {
  InsertUserPlatformReviewMutation as InsertUserPlatformReviewMutationResult,
  InsertUserPlatformReviewMutationVariables,
} from "@/graphql/generated/graphql";

// Once per app session — App.vue's auth watcher re-runs on every route change, same guard shape as
// useWelcomeFlow.composable.ts's own `checked`.
let checked = false;

/** Dev-only escape hatch: previews cannot wait for the start date, so `?platformReview=preview`
 * opens the card straight away without reading or writing anything. */
function isPreviewRequested(): boolean {
  if (!featureFlags.isDev) return false;
  return new URLSearchParams(window.location.search).get("platformReview") === "preview";
}

/** Resolves once no welcome tour is on screen — the review card must never open underneath one. */
async function waitForNoActiveWelcomeTour(): Promise<void> {
  const coordinationStore = useFlowGuideCoordinationStore();
  if (!coordinationStore.isWelcomeTourActive) return;

  await new Promise<void>((resolve) => {
    const stop = watch(
      () => coordinationStore.isWelcomeTourActive,
      (active) => {
        if (!active) {
          stop();
          resolve();
        }
      }
    );
  });
}

/** An existing account past the tour, the start date and the 7-day gap gets the card on a later desktop
 * visit, never under a welcome tour, until it answers or declines. `now` is injectable for tests. */
export async function checkAndShowPlatformReview(now: () => number = Date.now): Promise<void> {
  const store = usePlatformReviewStore();

  if (isPreviewRequested()) {
    store.open();
    return;
  }

  if (checked) return;

  // Same reasoning as useWelcomeFlow's checkAndShow: don't consume the once-per-session guard on a
  // viewport where the card (and everything it asks about) doesn't make sense to show.
  if (!useIsDesktop().value) return;
  checked = true;

  try {
    await waitForHasuraAuthenticated();
    await waitForNoActiveWelcomeTour();
    await waitForModalStackToEmpty();

    const platformRow = await getWelcomeFlowRow(WELCOME_FLOW_KEY);
    if (!platformRow || platformRow.variant !== "existing") return;
    if (!platformRow.completedAt) return;
    if (now() < PLATFORM_REVIEW_SHOW_FROM.getTime()) return;
    if (now() - new Date(platformRow.completedAt).getTime() < PLATFORM_REVIEW_MIN_GAP_MS) return;

    // Answered or declined: never again. A card that was only left open comes back, and the first
    // showing key still free says which showing this is; none free means the cap is used up.
    const reviewRows = await getWelcomeFlowRows([...PLATFORM_REVIEW_SHOW_KEYS, PLATFORM_REVIEW_NOT_NOW_KEY]);
    if (reviewRows.get(PLATFORM_REVIEW_FLOW_KEY)?.completedAt) return;
    if (reviewRows.has(PLATFORM_REVIEW_NOT_NOW_KEY)) return;
    const showKey = PLATFORM_REVIEW_SHOW_KEYS.find((key) => !reviewRows.has(key));
    if (!showKey) return;

    await insertWelcomeFlowRow(showKey, "existing");
    void recordWelcomeFlowEvent({ flowKey: PLATFORM_REVIEW_FLOW_KEY, event: "opened" });

    store.open();
  } catch (error) {
    if (error instanceof HasuraNotAuthenticatedError) return;
    reportSwallowedFailure("checkAndShowPlatformReview", error);
  }
}

/** Best-effort: the review write is production-only, so a rejected or skipped insert must never block
 * marking the once row complete. Only writes; the card owns its thank-you and closes itself. */
export async function submit(score: number, comment: string): Promise<void> {
  if (!featureFlags.isDev) {
    try {
      await gaiupApolloClient.mutate<InsertUserPlatformReviewMutationResult, InsertUserPlatformReviewMutationVariables>({
        mutation: InsertUserPlatformReviewMutation,
        variables: { flowKey: PLATFORM_REVIEW_FLOW_KEY, score, comment: comment.trim() || null },
        context: { tolerateMissingSchema: true },
      });
    } catch (error) {
      reportSwallowedFailure("insertUserPlatformReview", error);
    }
  }

  await markWelcomeFlowCompleted(PLATFORM_REVIEW_FLOW_KEY);
  void recordWelcomeFlowEvent({ flowKey: PLATFORM_REVIEW_FLOW_KEY, event: "completed" });
}

/** Not now: the cross is a decision, so it gets its own row and the card never returns; the abandoned
 * event is the log line beside it. Both best-effort. */
export function dismiss(): void {
  void insertWelcomeFlowRow(PLATFORM_REVIEW_NOT_NOW_KEY, "existing");
  void recordWelcomeFlowEvent({ flowKey: PLATFORM_REVIEW_FLOW_KEY, event: "abandoned" });
  usePlatformReviewStore().close();
}
